import React, { useMemo } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Skeleton } from 'src/components/ui/skeleton';
import { ConversionsByBeaconRow } from 'src/redux/admin/reporting/reporting.types';
import { formatTokenizedLabel } from 'src/pages/reports/utils/format-reporting-labels';

interface ConversionsByBeaconChartProps {
  data: ConversionsByBeaconRow[];
  isLoading?: boolean;
  className?: string;
}

function formatBeaconLabel(row: ConversionsByBeaconRow): string {
  const name = row.conversion_name?.trim();
  if (name) {
    return formatTokenizedLabel(name);
  }
  return row.conversion_id;
}

const ConversionsByBeaconChart: React.FC<ConversionsByBeaconChartProps> = ({
  data,
  isLoading = false,
  className = '',
}) => {
  const chartData = useMemo(
    () =>
      data
        .map((row) => ({
          label: formatBeaconLabel(row),
          conversions: Number(row.conversions ?? 0),
          conversionType: row.conversion_type
            ? formatTokenizedLabel(row.conversion_type)
            : undefined,
        }))
        .filter((row) => row.label && row.conversions > 0),
    [data],
  );

  if (!isLoading && chartData.length === 0) {
    return null;
  }

  return (
    <div className={`overflow-hidden rounded-xl border border-[#EDEEF2] bg-white ${className}`}>
      <div className="border-b border-[#EDEEF2] px-5 py-4">
        <div className="text-sm font-bold tracking-[-0.2px] text-[#0D0F1A]">
          Conversions by Beacon
        </div>
        <div className="mt-0.5 text-xs text-[#9CA3AF]">
          Conversion counts grouped by beacon for the selected period
        </div>
      </div>
      <div className="p-5">
        {isLoading ? (
          <Skeleton className="h-[260px] w-full" />
        ) : (
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} layout="vertical" margin={{ left: 12, right: 12 }}>
                <CartesianGrid stroke="#EDEEF2" horizontal={false} />
                <XAxis
                  type="number"
                  tick={{ fill: '#8A8EA0', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  type="category"
                  dataKey="label"
                  width={160}
                  tick={{ fill: '#5E6278', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  formatter={(value: number) => [value.toLocaleString(), 'Conversions']}
                  labelFormatter={(label, payload) => {
                    const conversionType = payload?.[0]?.payload?.conversionType;
                    return conversionType ? `${label} (${conversionType})` : String(label);
                  }}
                  contentStyle={{
                    borderRadius: 8,
                    border: '1px solid #EDEEF2',
                    fontSize: 12,
                  }}
                />
                <Bar
                  dataKey="conversions"
                  name="Conversions"
                  fill="#2563EB"
                  radius={[0, 4, 4, 0]}
                  isAnimationActive={false}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversionsByBeaconChart;
